import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [orderNumber, setOrderNumber] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('error');
      setError('No session ID found');
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('error');
      setError('Payment verification timed out. Please contact support.');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/stripe/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setOrderNumber(data.order_number || 'Processing...');
        setStatus('success');
        
        // Clear cart
        localStorage.removeItem('cart_session_id');
        localStorage.setItem('cart_count', '0');
        window.dispatchEvent(new Event('cartUpdated'));
        return;
      } else if (data.status === 'expired') {
        setStatus('error');
        setError('Payment session expired. Please try again.');
        return;
      }

      // Continue polling if still pending
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
      setError('Error verifying payment. Please contact support.');
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-dark)' }}>
      <Header />
      
      <section className="pt-32 pb-16 flex items-center justify-center min-h-[60vh]">
        <div className="max-w-lg mx-auto px-4 sm:px-6 text-center">
          <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl p-8 sm:p-12">
            
            {status === 'checking' && (
              <>
                <div className="w-24 h-24 rounded-full bg-[#3B82F6]/20 flex items-center justify-center mx-auto mb-8 border-4 border-[#3B82F6]">
                  <Loader2 size={48} className="text-[#3B82F6] animate-spin" />
                </div>
                
                <h1 className="text-3xl font-bold text-[var(--text-primary)] mb-4">
                  Verifying Payment...
                </h1>
                
                <p className="text-[var(--text-muted)]">
                  Please wait while we confirm your payment.
                </p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="w-24 h-24 rounded-full bg-[#10B981]/20 flex items-center justify-center mx-auto mb-8 border-4 border-[#10B981]">
                  <Check size={48} className="text-[#10B981]" />
                </div>
                
                <h1 className="text-3xl sm:text-4xl font-bold text-[var(--text-primary)] mb-4">
                  Payment Successful!
                </h1>
                
                <p className="text-lg text-[var(--text-secondary)] mb-6">
                  Thank you for your purchase!
                </p>
                
                <div className="bg-[var(--bg-dark)] border border-[var(--border)] rounded-xl p-6 mb-8">
                  <p className="text-[var(--text-muted)] mb-2">Order Number</p>
                  <p className="text-2xl font-bold bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] bg-clip-text text-transparent">
                    {orderNumber}
                  </p>
                </div>
                
                <p className="text-[var(--text-muted)] mb-8">
                  We've sent a confirmation email with your order details. Our team will contact you within 24 hours.
                </p>
                
                <button
                  onClick={() => navigate('/')}
                  data-testid="back-to-home-btn"
                  className="px-8 py-4 text-lg font-semibold text-white rounded-xl transition-all hover:scale-105 hover:shadow-xl"
                  style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }}
                >
                  Back to Home
                </button>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="w-24 h-24 rounded-full bg-[#EF4444]/20 flex items-center justify-center mx-auto mb-8 border-4 border-[#EF4444]">
                  <AlertCircle size={48} className="text-[#EF4444]" />
                </div>
                
                <h1 className="text-3xl font-bold text-[var(--text-primary)] mb-4">
                  Something Went Wrong
                </h1>
                
                <p className="text-[var(--text-muted)] mb-8">
                  {error}
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => navigate('/checkout')}
                    className="px-6 py-3 font-semibold text-white rounded-xl transition-all hover:scale-105"
                    style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }}
                  >
                    Try Again
                  </button>
                  <button
                    onClick={() => navigate('/')}
                    className="px-6 py-3 font-semibold text-[var(--text-secondary)] border border-[var(--border)] rounded-xl transition-all hover:border-[var(--border-hover)]"
                  >
                    Back to Home
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default CheckoutSuccess;
